import React from 'react';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@emotion/react';
import makeStagedProgressBarStyling from './makeStagedProgressBarStyling';
import StartIcon from './icons/StartIcon';
import RequirementsIcon from './icons/RequirementsIcon';
import ContactInfoIcon from './icons/ContactInfoIcon';
import FinishIcon from './icons/FinishIcon';

const StagedProgressBar = ({ stages = [] }) => {
  const theme = useTheme();
  const styling = makeStagedProgressBarStyling({ theme });
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  if (!stages || stages.length === 0) return null;

  // Map stage IDs to their respective icon components
  const stageIcons = {
    start: StartIcon,
    requirements: RequirementsIcon,
    contactInfo: ContactInfoIcon,
    finish: FinishIcon,
  };

  // Add icons to stages if they don't already have them
  const stagesWithIcons = stages.map((stage) => {
    if (stage.icon) return stage;

    const IconComponent = stageIcons[stage.id];
    return {
      ...stage,
      icon: IconComponent ? <IconComponent /> : null,
    };
  });

  // Find the index of the current stage for connector styling and label display
  const currentIndex = stagesWithIcons.findIndex(
    (stage) => stage.status === 'CURRENT'
  );
  // Find the current stage for label display
  const currentStage = stagesWithIcons.find(
    (stage) => stage.status === 'CURRENT'
  );

  return (
    <div className="staged-progress-wrapper" style={styling.progressWrapper}>
      <div className="progress-container" style={styling.progressContainer}>
        {stagesWithIcons.map((stage, index) => {
          const isCompleted = stage.status === 'COMPLETED';
          const isCurrent = stage.status === 'CURRENT';

          return (
            <div
              className="stage"
              style={styling.stageStyle(
                isCompleted,
                isCurrent,
                index < stagesWithIcons.length - 1
              )}
              key={stage.id}
              data-testid={`stagedProgressBarStep-${index}`}
              data-status={stage.status.toLowerCase()}
            >
              <div className="icon-wrapper" style={styling.iconWrapper}>
                <div
                  className="step-icon"
                  style={styling.stepIcon(isCompleted, isCurrent)}
                  data-testid={`stagedProgressBarStepIcon-${index}`}
                  data-status={stage.status.toLowerCase()}
                >
                  {stage.icon}
                </div>
                {index < stagesWithIcons.length - 1 && (
                  <div
                    className="connector"
                    data-testid={`stagedProgressBarConnector-${index}`}
                    style={styling.connector(
                      index < currentIndex || isCompleted
                    )}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Label container with positioned labels */}
      {isDesktop ? (
        <div className="labels-container" style={styling.labelsContainer}>
          {stagesWithIcons.map((stage, index) => (
            <div
              key={`label-${stage.id}`}
              className="stage-label"
              style={styling.stageLabel(
                stage.status === 'CURRENT',
                index === 0,
                index === stagesWithIcons.length - 1,
                index,
                stagesWithIcons.length
              )}
            >
              {stage.status === 'CURRENT' && stage.label}
            </div>
          ))}
        </div>
      ) : (
        currentStage && (
          <div
            className="current-stage-label"
            style={styling.currentStageLabel}
          >
            {currentStage.label}
          </div>
        )
      )}
    </div>
  );
};

export default StagedProgressBar;
